/**
 * GemTypeRegistry - Dynamic Gem Type Management Service
 * Implements Single Responsibility Principle for gem type configuration management
 */
import { DEFAULT_GEM_TYPE_CONFIGS, FALLBACK_GEM_TYPE_CONFIG, DEFAULT_PRIORITY_ORDER, DYNAMIC_GEM_THEME_COLORS } from '@/config/defaultGemTypes';
import { themeStore } from '@/stores/themeStore';
export class GemTypeRegistry {
    typeConfigs = new Map();
    customConfigs = new Map();
    colorIndex = 0;
    constructor() {
        this.initializeDefaultTypes();
    }
    /**
     * Initialize registry with default gem type configurations
     */
    initializeDefaultTypes() {
        Object.entries(DEFAULT_GEM_TYPE_CONFIGS).forEach(([name, config]) => {
            // Store with both original case and lowercase for better matching
            this.typeConfigs.set(name, config);
            this.typeConfigs.set(name.toLowerCase(), config);
        });
    }
    /**
     * Register a new gem type or override existing configuration
     * @param name - Type name (case insensitive)
     * @param config - Type configuration
     */
    registerType(name, config) {
        const normalizedName = name.toLowerCase();
        // Merge with fallback config to ensure all required fields
        const mergedConfig = this.mergeConfigs(FALLBACK_GEM_TYPE_CONFIG, config);
        // Store in custom configs for tracking
        this.customConfigs.set(normalizedName, mergedConfig);
        this.typeConfigs.set(normalizedName, mergedConfig);
    }
    /**
     * Get configuration for a specific gem type
     * @param name - Type name (case insensitive)
     * @returns Type configuration or fallback
     */
    getTypeConfig(name) {
        // Try exact match first, then lowercase
        let config = this.typeConfigs.get(name);
        if (!config) {
            config = this.typeConfigs.get(name.toLowerCase());
        }
        if (config) {
            return config;
        }
        // Generate dynamic configuration with consistent hash-based color
        const dynamicColors = this.getConsistentColorForType(name);
        const dynamicConfig = {
            ...FALLBACK_GEM_TYPE_CONFIG,
            displayName: this.generateDisplayName(name),
            color: dynamicColors.color,
            backgroundColor: dynamicColors.backgroundColor,
            isCustom: true
        };
        return dynamicConfig;
    }
    /**
     * Get all registered gem types
     * @returns Array of all gem type configurations
     */
    getAllTypes() {
        const types = [];
        this.typeConfigs.forEach((config, name) => {
            const isCustom = this.customConfigs.has(name);
            const source = isCustom ? 'session' : 'default';
            types.push({
                name: name.toUpperCase(),
                count: 0,
                config,
                isCustom,
                source
            });
        });
        return this.sortTypesByPriority(types);
    }
    /**
     * Discover gem types from session data and return configured types
     * @param gems - Session gems data
     * @returns Array of discovered gem types with configurations
     */
    discoverTypes(gems) {
        const discoveredTypes = [];
        Object.entries(gems).forEach(([typeName, items]) => {
            if (!Array.isArray(items) || items.length === 0) {
                return; // Skip invalid or empty types
            }
            const config = this.getTypeConfig(typeName);
            const isCustom = !this.typeConfigs.has(typeName.toLowerCase());
            discoveredTypes.push({
                name: typeName,
                count: items.length,
                config,
                isCustom,
                source: isCustom ? 'session' : 'default'
            });
        });
        return this.sortTypesByPriority(discoveredTypes);
    }
    /**
     * Merge two gem type configurations
     * @param base - Base configuration
     * @param override - Override configuration
     * @returns Merged configuration
     */
    mergeConfigs(base, override) {
        return {
            ...base,
            ...override,
            // Deep merge contexts if both exist
            contexts: {
                ...base.contexts,
                ...override.contexts
            }
        };
    }
    /**
     * Register multiple types from session context
     * @param sessionGems - Session gems data
     * @param sessionConfig - Session-specific configurations
     */
    registerSessionTypes(sessionGems, sessionConfig) {
        Object.keys(sessionGems).forEach(typeName => {
            const customConfig = sessionConfig?.[typeName] || {};
            // Only register if custom config is provided with meaningful data
            // This allows getTypeConfig to generate dynamic configs for unknown types
            if (Object.keys(customConfig).length > 0) {
                this.registerType(typeName, customConfig);
            }
            // For types without custom config, let getTypeConfig generate them dynamically
        });
    }
    /**
     * Generate a human-readable display name from type name with emoji
     * @param typeName - Raw type name
     * @returns Formatted display name with emoji
     */
    generateDisplayName(typeName) {
        // Use smart emoji detection based on type name
        const emoji = this.generateEmojiForType(typeName);
        const formattedName = typeName
            .split(/[-_]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        return `${emoji} ${formattedName}`;
    }
    /**
     * Get consistent color for a gem type using hash-based selection
     * @param typeName - Raw type name
     * @returns Color configuration
     */
    getConsistentColorForType(typeName) {
        // Simple hash function for consistent color selection
        let hash = 0;
        for (let i = 0; i < typeName.length; i++) {
            const char = typeName.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        // Use absolute value and modulo to get consistent index
        const colorIndex = Math.abs(hash) % DYNAMIC_GEM_THEME_COLORS.length;
        const themeColors = DYNAMIC_GEM_THEME_COLORS[colorIndex];
        // Return colors based on current theme
        return themeColors[themeStore.theme];
    }
    /**
     * Generate an appropriate emoji for a custom gem type
     * @param typeName - Raw type name
     * @returns Emoji character
     */
    generateEmojiForType(typeName) {
        const lowerName = typeName.toLowerCase();
        // Emoji mapping based on common keywords
        if (lowerName.includes('compan') || lowerName.includes('org') || lowerName.includes('business'))
            return '🏢';
        if (lowerName.includes('person') || lowerName.includes('people') || lowerName.includes('contact'))
            return '👤';
        if (lowerName.includes('project') || lowerName.includes('initiative') || lowerName.includes('goal'))
            return '🚀';
        if (lowerName.includes('tool') || lowerName.includes('tech') || lowerName.includes('software'))
            return '🔧';
        if (lowerName.includes('insight') || lowerName.includes('pattern') || lowerName.includes('observation'))
            return '💡';
        if (lowerName.includes('question') || lowerName.includes('assumption') || lowerName.includes('doubt'))
            return '❓';
        if (lowerName.includes('action') || lowerName.includes('task') || lowerName.includes('todo'))
            return '✅';
        if (lowerName.includes('decision') || lowerName.includes('choice') || lowerName.includes('verdict'))
            return '⚖️';
        if (lowerName.includes('risk') || lowerName.includes('issue') || lowerName.includes('problem'))
            return '⚠️';
        if (lowerName.includes('opportunit') || lowerName.includes('chance') || lowerName.includes('potential'))
            return '🎯';
        if (lowerName.includes('idea') || lowerName.includes('concept') || lowerName.includes('thought'))
            return '💭';
        if (lowerName.includes('meeting') || lowerName.includes('discussion') || lowerName.includes('conversation'))
            return '💬';
        if (lowerName.includes('document') || lowerName.includes('file') || lowerName.includes('paper'))
            return '📄';
        if (lowerName.includes('timeline') || lowerName.includes('schedule') || lowerName.includes('deadline'))
            return '📅';
        if (lowerName.includes('money') || lowerName.includes('budget') || lowerName.includes('cost'))
            return '💰';
        if (lowerName.includes('location') || lowerName.includes('place') || lowerName.includes('venue'))
            return '📍';
        if (lowerName.includes('link') || lowerName.includes('url') || lowerName.includes('resource'))
            return '🔗';
        if (lowerName.includes('note') || lowerName.includes('memo') || lowerName.includes('reminder'))
            return '📝';
        if (lowerName.includes('milestone') || lowerName.includes('achievement') || lowerName.includes('success'))
            return '🏆';
        if (lowerName.includes('challenge') || lowerName.includes('obstacle') || lowerName.includes('barrier'))
            return '🚧';
        if (lowerName === 'question')
            return '❓';
        if (lowerName.includes('potential') && lowerName.includes('risk'))
            return '⚠️';
        if (lowerName.includes('resource') && lowerName.includes('link'))
            return '🔗';
        // Default fallback - generic gem emoji
        return '💎';
    }
    /**
     * Sort gem types by priority and custom order
     * @param types - Array of gem types to sort
     * @returns Sorted array
     */
    sortTypesByPriority(types) {
        return types.sort((a, b) => {
            // First, sort by explicit priority
            const priorityA = a.config.priority || 999;
            const priorityB = b.config.priority || 999;
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }
            // Then by default order if available
            const orderA = DEFAULT_PRIORITY_ORDER.indexOf(a.name);
            const orderB = DEFAULT_PRIORITY_ORDER.indexOf(b.name);
            if (orderA !== -1 && orderB !== -1) {
                return orderA - orderB;
            }
            if (orderA !== -1)
                return -1;
            if (orderB !== -1)
                return 1;
            // Finally by item count (descending)
            return b.count - a.count;
        });
    }
    /**
     * Clear all custom configurations (keeps defaults)
     */
    clearCustomTypes() {
        this.customConfigs.clear();
        this.typeConfigs.clear();
        this.initializeDefaultTypes();
    }
    /**
     * Get statistics about registered types
     */
    getRegistryStats() {
        return {
            totalTypes: this.typeConfigs.size,
            defaultTypes: this.typeConfigs.size - this.customConfigs.size,
            customTypes: this.customConfigs.size,
            typeNames: Array.from(this.typeConfigs.keys())
        };
    }
}
// Singleton instance for global use
export const gemTypeRegistry = new GemTypeRegistry();
